import { Button, Grid, Popover } from 'antd';
import { useCallback, useMemo, useState } from 'react';

import type { TaskPreviewItemData } from '@app/components/LeadCard/data';
import { TaskPreviewItem } from '@app/components/LeadCard/ui/TaskPreviewItem';

import type { FC } from 'react';

import './TasksPreview.scss';

const VISIBLE_TASKS_COUNT = 2;
const { useBreakpoint } = Grid;

const getTasksPopupContainer = (triggerNode: HTMLElement): HTMLElement =>
  triggerNode.closest<HTMLElement>('.LeadCard') ?? document.body;

interface TasksPreviewProps {
  tasks: TaskPreviewItemData[];
}

export const TasksPreview: FC<TasksPreviewProps> = ({ tasks }) => {
  const [areTasksExpanded, setAreTasksExpanded] = useState(false);
  const [isTasksPopoverOpen, setIsTasksPopoverOpen] = useState(false);
  const screens = useBreakpoint();

  const shouldUseTasksPopover = useMemo(() => !screens.xl, [screens]);

  const visibleTasks = useMemo(
    () =>
      areTasksExpanded && !shouldUseTasksPopover
        ? tasks
        : tasks.slice(0, VISIBLE_TASKS_COUNT),
    [tasks, areTasksExpanded, shouldUseTasksPopover]
  );

  const hasTasks = useMemo(() => tasks.length > 0, [tasks]);
  const canToggleTasks = useMemo(
    () => tasks.length > VISIBLE_TASKS_COUNT,
    [tasks]
  );

  const toggleTasksHandler = useCallback(() => {
    if (shouldUseTasksPopover) {
      setIsTasksPopoverOpen(true);

      return;
    }

    setAreTasksExpanded((prevValue) => !prevValue);
  }, [shouldUseTasksPopover]);

  const tasksPopoverOpenChangeHandler = useCallback((open: boolean) => {
    setIsTasksPopoverOpen(open);
  }, []);

  const tasksPopoverContent = (
    <div className="TasksPreview__popover-list">
      {tasks.map((task) => (
        <TaskPreviewItem task={task} compact key={task.id} />
      ))}
    </div>
  );

  return (
    <section className="TasksPreview">
      {hasTasks && (
        <div className="TasksPreview__list">
          {visibleTasks.map((task) => (
            <TaskPreviewItem task={task} key={task.id} />
          ))}
        </div>
      )}
      {canToggleTasks && shouldUseTasksPopover && (
        <Popover
          content={tasksPopoverContent}
          trigger="click"
          open={isTasksPopoverOpen}
          onOpenChange={tasksPopoverOpenChangeHandler}
          placement="topLeft"
          arrow={false}
          getPopupContainer={getTasksPopupContainer}
          classNames={{
            root: 'TasksPreview__popover',
            body: 'TasksPreview__popover-body',
          }}
        >
          <Button
            className="TasksPreview__toggle"
            type="link"
            onClick={toggleTasksHandler}
          >
            Показать все задачи
          </Button>
        </Popover>
      )}

      {canToggleTasks && !shouldUseTasksPopover && (
        <Button
          className="TasksPreview__toggle"
          type="link"
          onClick={toggleTasksHandler}
        >
          {areTasksExpanded ? 'Скрыть задачи' : 'Показать все задачи'}
        </Button>
      )}
    </section>
  );
};
